/* eslint-disable @typescript-eslint/camelcase */
import axios, { AxiosRequestConfig } from 'axios';
import i18n from 'i18n';
import {
  authenticateInvalidateSession,
  authenticateLogout,
} from 'modules/authenticate/actions';
import { messagesRequest } from 'modules/messages/actions';
import { getErrorsRequest } from 'modules/errors/actions';
import { modalOpen } from 'modules/modal/actions';
import { updatePermissionsRequest } from 'modules/operators/actions';
import qs from 'qs';
import { store } from 'store';
import { isOciLicenseTypeRequiresWindowsError } from 'modules/environments/modelErrorMessages';
import { getErrorMessageKey, getErrorsFields } from 'utils/helpers';
import { ApiCall } from './interfaces';

const LOGOUT_DELAY = 1500;

const axiosErrorsInterceptor = async (req: any) => {
  if (!req.response) {
    return Promise.reject(new Error());
  }

  if (req.response.data.connected === false) {
    return Promise.reject(req);
  }
  if (
    req.response.status === 400 &&
    req.response.data.message.code === 'publication_already_in_progress'
  ) {
    store.dispatch(
      messagesRequest({
        type: 'info',
        message: i18n.t(getErrorMessageKey(req)),
      })
    );
  } else if (
    req.response.status === 401 &&
    req.response.data.message.code === 'authentication_failed'
  ) {
    store.dispatch(authenticateInvalidateSession());
  } else if (
    req.response.data.message === 'Code not found' ||
    req.response.data.message === 'Expired code'
  ) {
    store.dispatch(
      messagesRequest({
        type: 'error',
        message: i18n.t(
          req.response.data.message === 'Expired code'
            ? 'INVALID_TOKEN_KEY'
            : 'EXPIRED_CODE'
        ),
      })
    );
  } else if (req.response.status === 403) {
    if (req.response.data.message.code === 'operator_is_blocked') {
      store.dispatch(
        messagesRequest({
          type: 'error',
          message: i18n.t(getErrorMessageKey(req)),
        })
      );
      setTimeout(() => {
        if (window.location.pathname !== '/') {
          store.dispatch(authenticateLogout());
        }
      }, LOGOUT_DELAY);
    }

    const storageState = store.getState();
    store.dispatch(
      modalOpen({
        title: i18n.t('DO_NOT_HAVE_PERMISSION_KEY'),
        maxWidth: 'sm',
        content: i18n.t('DO_NOT_HAVE_PERMISSION_MSG_KEY'),
        genericFooter: {
          action: () => {
            store.dispatch(
              updatePermissionsRequest(storageState.authenticate.data)
            );
          },
          label: 'Ok',
        },
      })
    );
  } else if (
    req.response.status === 404 &&
    window.location.pathname !== '/' &&
    req.response.data.message.code === 'operator_does_not_exist'
  ) {
    store.dispatch(
      messagesRequest({
        type: 'error',
        message: i18n.t(getErrorMessageKey(req)),
      })
    );
    setTimeout(() => {
      store.dispatch(authenticateLogout());
    }, LOGOUT_DELAY);
  } else if (req.response.data.message.code === 'monitoring_status_error') {
    return Promise.reject(req);
  } else if (isOciLicenseTypeRequiresWindowsError(req)) {
    store.dispatch(getErrorsRequest({ error: getErrorsFields(req) }));
    return Promise.reject(req);
  } else {
    store.dispatch(
      messagesRequest({
        type: 'error',
        message: i18n.t(getErrorMessageKey(req), req?.response?.data),
      })
    );
    store.dispatch(getErrorsRequest({ error: getErrorsFields(req) }));
  }

  return Promise.reject(req);
};

const request = async (params: AxiosRequestConfig) => {
  const { authenticate } = await store.getState();

  const LOGGED_REGION_KEY = 'logged_region';
  const REGION_DEFAULT = localStorage.getItem(LOGGED_REGION_KEY);

  if (REGION_DEFAULT) {
    // eslint-disable-next-line no-param-reassign
    params.baseURL = `${JSON.parse(REGION_DEFAULT).url}/api/v3`;
  }

  let instance;

  const isLoginRequest = params.url === '/authenticate/';

  if (authenticate.data?.token && !isLoginRequest) {
    instance = axios.create({
      ...params,

      headers: {
        ...params.headers,
        Authorization: `Token ${authenticate.data.token}`,
      },
      data: qs.stringify(params.data),
    });
  } else {
    instance = axios.create({
      ...params,

      data: qs.stringify(params.data),
    });
  }

  instance.interceptors.response.use(
    response => response,
    axiosErrorsInterceptor
  );

  return instance(params);
};

interface BatchRequestsProps {
  url: string;
  request_id?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, string>;
  data?: Record<string, string>;
}

export const batchRequests = async (props: BatchRequestsProps[]) => {
  const requests = props.map(({ url, query, data, ...rest }) => ({
    ...rest,
    endpoint: `/api/v3${url.startsWith('/') ? url : `/${url}`}`,
    url_params: query,
    json_data: data,
  }));

  const { status, data } = await request({
    baseURL: process.env.REACT_APP_URL || '',
    method: 'POST',
    url: '/batch/authenticated',
    data: {
      requests,
    },
  });

  const responses = data.responses.map(
    ({ status_code, json_data, ...rest }) => {
      const response = {
        ...rest,
        status: status_code,
        data: json_data,
      };

      if (response.status >= 400) {
        store.dispatch(
          messagesRequest({
            type: 'error',
            message: i18n.t(getErrorMessageKey({ response }), response.data),
          })
        );
        store.dispatch(
          getErrorsRequest({ error: getErrorsFields({ response }) })
        );
      }

      return response;
    }
  );

  return { status, data: responses };
};

export default ({ baseURL = process.env.REACT_APP_URL || '' }: ApiCall) => ({
  get: (props: any) =>
    request({
      baseURL,
      ...props,
      method: 'GET',
    }),
  post: (props: any) =>
    request({
      baseURL,
      ...props,
      method: 'POST',
    }),
  put: (props: any) =>
    request({
      baseURL,
      ...props,
      method: 'PUT',
    }),
  patch: (props: any) =>
    request({
      baseURL,
      ...props,
      method: 'PATCH',
    }),
  delete: (props: any) =>
    request({
      baseURL,
      ...props,
      method: 'DELETE',
    }),
});
